/**
 * Outbound-text encoding-artifact normalization (UI twin of the backend
 * `normalize_outbound_text` in the agents service).
 *
 * Agent-produced customer-facing text occasionally arrives carrying *literal*
 * encoding artifacts rather than the characters they represent:
 *
 *   - Unicode escape sequences that were never decoded, e.g. the characters
 *     `W e \ u 2 0 1 9` instead of `We’` — these reach the model via an
 *     `ensure_ascii=True` serialized transcript and get mirrored verbatim into
 *     the next tool call, so they land in the displayed `response_text`.
 *   - CP1252-as-UTF-8 "mojibake", e.g. `â€™` instead of `’`.
 *
 * The Approval Details panel must show the operator exactly what will be posted
 * to Apple. The backend deterministically repairs these before posting, so the
 * UI applies the *same* repair and flags that it happened — giving the operator
 * confidence that the displayed (clean) text matches what gets sent, with a
 * clear signal that the raw payload contained an artifact.
 *
 * Scope is deliberately narrow (Unicode escapes + mojibake only). We do NOT
 * touch `\n`/`\t`/`\xNN` sequences: real copy can legitimately contain a
 * backslash (quoted path, regex), and the observed corruption left genuine
 * newlines intact. Rewriting those would risk mangling valid content.
 *
 * Keep this in semantic lockstep with the Python implementation at
 * services/agents/backend/internal/adapters/agent_tools/catalog/_text_normalization.py
 */

const UNICODE_ESCAPE_RE = /\\u([0-9a-fA-F]{4})/g
const LONG_UNICODE_ESCAPE_RE = /\\U([0-9a-fA-F]{8})/g

// Telltale markers of CP1252-as-UTF-8 mojibake. Only attempt the repair when
// one is present so clean text is never round-tripped.
const MOJIBAKE_MARKERS = ['â€', 'Ã', 'â‚¬', 'Â']

// Reverse map for the CP1252 0x80–0x9F range, whose characters map to high
// Unicode code points (unlike Latin-1 where charCode === byte). Needed to turn
// a CP1252-decoded character back into its original byte before re-decoding as
// UTF-8. Code points outside this map with charCode <= 0xff use the identity
// byte; anything else is not representable as a single CP1252 byte.
const CP1252_REVERSE: Record<number, number> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
  0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
  0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
  0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
  0x017e: 0x9e, 0x0178: 0x9f,
}

export type NormalizeResult = {
  /** The repaired text (equal to the input when no artifacts were found). */
  text: string
  /** True when at least one artifact was repaired. */
  repaired: boolean
  /** Artifact-kind tags describing what was fixed. */
  repairs: string[]
}

/**
 * Decode `\uXXXX` / `\UXXXXXXXX` escapes. JavaScript strings are UTF-16, so
 * `String.fromCharCode` on each 4-hex unit yields surrogate code units that
 * combine naturally into the correct astral character when adjacent.
 */
function decodeUnicodeEscapes(input: string): string {
  let out = input.replace(UNICODE_ESCAPE_RE, (_m, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  )
  out = out.replace(LONG_UNICODE_ESCAPE_RE, (_m, hex: string) =>
    String.fromCodePoint(parseInt(hex, 16)),
  )
  return out
}

/**
 * Repair CP1252-as-UTF-8 mojibake when safely reversible: re-encode the string
 * as Latin-1/CP1252 bytes and decode as UTF-8. Returns the original on failure.
 */
function fixMojibake(input: string): string {
  if (!MOJIBAKE_MARKERS.some((m) => input.includes(m))) {
    return input
  }
  try {
    const bytes = new Uint8Array(input.length)
    for (let i = 0; i < input.length; i++) {
      const code = input.charCodeAt(i)
      const mapped = CP1252_REVERSE[code]
      if (mapped !== undefined) {
        bytes[i] = mapped
      } else if (code <= 0xff) {
        bytes[i] = code
      } else {
        // Not representable as a single CP1252 byte — not classic mojibake.
        return input
      }
    }
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    return decoded
  } catch {
    return input
  }
}

/**
 * Repair encoding artifacts in a string. Idempotent: clean input is returned
 * unchanged with `repaired: false`.
 */
export function normalizeArtifacts(input: unknown): NormalizeResult {
  if (typeof input !== 'string' || input.length === 0) {
    return { text: typeof input === 'string' ? input : '', repaired: false, repairs: [] }
  }

  const repairs: string[] = []
  let out = input

  if (UNICODE_ESCAPE_RE.test(out) || LONG_UNICODE_ESCAPE_RE.test(out)) {
    // Reset lastIndex — the `.test()` calls above advance it on global regexes.
    UNICODE_ESCAPE_RE.lastIndex = 0
    LONG_UNICODE_ESCAPE_RE.lastIndex = 0
    out = decodeUnicodeEscapes(out)
    repairs.push('unicode_escape')
  }

  const fixed = fixMojibake(out)
  if (fixed !== out) {
    out = fixed
    repairs.push('mojibake')
  }

  return { text: out, repaired: repairs.length > 0, repairs }
}
